/**
 * Where the scrubber's note markers come from: the full anchored-message set
 * (every page of the thread) plus the chat's first page, which is newer than
 * any refetch of the former — a just-sent note shows at once.
 */
import type { MessageView } from '@discovr/types';

import { mapThreadMessages } from '../../../mapThreadMessages';
import type { MarkerSourceEntry } from './buildMarkers';

function createdAtMs(entry: MarkerSourceEntry): number {
  return Date.parse(entry.created_at);
}

/**
 * The chat is authoritative for the span it covers (a note it no longer
 * lists was deleted); the anchored set only adds what is older than that span.
 * Without the anchored set (loading, or an older backend) the chat stands alone.
 */
export function mergeMomentSources(chat: MarkerSourceEntry[], anchored: MessageView[] | undefined): MarkerSourceEntry[] {
  if (!anchored || anchored.length === 0) return chat;
  const inChat = new Set(chat.map((entry) => entry.id));
  const windowStartMs = Math.min(...chat.map(createdAtMs));
  function isOlderThanChat(entry: MarkerSourceEntry): boolean {
    if (inChat.has(entry.id)) return false;
    return chat.length === 0 || createdAtMs(entry) < windowStartMs;
  }
  const older = mapThreadMessages({ messages: anchored }).filter(isOlderThanChat);
  if (older.length === 0) return chat;
  return [...chat, ...older];
}
